import {
  LoaderFunction,
  useLoaderData,
  useRouteLoaderData,
} from "react-router-dom";
import {
  CoursesContext,
  ProgramFeature,
} from "common/api/decimal/methods/course.ts";
import {
  Cart as StoreCart,
  getCart as getStoreCart,
} from "common/api/decimal/methods/cart.ts";
import { getCoursesExtras } from "../../homeContext.ts";

declare global {
  interface Window {
    context: {
      token: string | null;
    };
  }
}

export interface MyCoursesConfig {
  assets: {
    showPrintPickup: boolean;
    labels: {
      library: string;
      oer: string;
      web: string;
    };
    notFound: string;
  };
  equitable_access: {
    programName: string;
    programDescription: string;
    features: ProgramFeature[];
    akademos: {
      updateShippingLink: string;
      personalizedValueSheet: string;
    };
    catalogLevelOptOut: boolean;
    optOutMessage: string | null;
  };
  inclusive_access: {
    programName: string;
    programDescription: string;
    features: ProgramFeature[];
  };
  no_program: {
    programName: string;
    programDescription: string;
  };
  lms: {
    label: string;
  };
  userSurvey: {
    show: boolean;
    url: string;
  };
}

export interface MyCoursesLayoutData {
  config: MyCoursesConfig;
  accessToken: string;
  storeCart?: StoreCart;
}

const getBrandConfig = (brand: CoursesContext["brand"] | null) => {
  if (!brand) {
    return null;
  }
  return {
    assets: {
      showPrintPickup: !brand.printPickup.disabled,
      labels: {
        library: brand.assets.labels.library,
        oer: brand.assets.labels.oer,
        web: brand.assets.labels.web,
      },
      notFound: brand.assets.notFound,
    },
    equitable_access: {
      programName: brand.ea.programName,
      programDescription: brand.ea.programDescription,
      features: brand.ea.programFeatures.filter((feature) => !!feature.text),
      akademos: {
        updateShippingLink: brand.ea.akademos.updateShippingLink,
        personalizedValueSheet: brand.ea.akademos.personalizedValueSheet,
      },
      catalogLevelOptOut: brand.ea.catalogLevelOptOut,
      optOutMessage: brand.ea.optOutMessage,
    },
    inclusive_access: {
      programName: brand.iaProgramName,
      programDescription: brand.iaProgramDescription,
      features: brand.iaProgramFeatures.filter((feature) => !!feature.text),
    },
    no_program: {
      programName: brand.optOutProgramName,
      programDescription: brand.optOutProgramDescription,
    },
    lms: {
      label: brand.lms.label,
    },
    userSurvey: {
      show: brand.userSurvey.showToast,
      url: brand.userSurvey.url,
    },
  };
};

export function useLayoutData(): MyCoursesLayoutData {
  return useRouteLoaderData("courses-root") as ReturnType<
    typeof useMyCoursesLayoutData
  >;
}

export function useMyCoursesLayoutData(): MyCoursesLayoutData {
  return useLoaderData() as MyCoursesLayoutData;
}

export const loadMyCoursesLayout: LoaderFunction = () => {
  // todo only load one cart based on brand flag?
  return Promise.all([getCoursesExtras(), getStoreCart()]).then(
    ([context, storeCart]) => {
      const config = context.accessToken && getBrandConfig(context?.brand);
      if (!config) {
        throw new Response("Not Found", {
          status: 404,
          statusText: "Brand Not Found",
        });
      }
      const layoutData: MyCoursesLayoutData = {
        config,
        accessToken: context?.accessToken ?? "",
        storeCart: storeCart?.data,
      };
      return layoutData;
    }
  );
};
